import { useContext, ReactElement } from 'react'

// Functions
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'

// Styles
import breakpoints from '@/assets/theme/breakpoints'
import classes from './PrivacyView-CSS'
import themeColors from '@/assets/theme/colors'

export default function PrivacyView() {
  const { t } = useTranslation('privacy')
  const { screenDimensions } = useContext(GlobalContext)

  const h3 = (text: string) => (
    <Typography
      variant="h3"
      sx={classes.h3}
      style={{
        fontSize:
          screenDimensions.width > breakpoints.mobileSmall
            ? '1.1rem'
            : '0.95rem'
      }}
    >
      {text}
    </Typography>
  )

  const h3Decoration = (text: string) => (
    <Typography
      variant="h3"
      sx={classes.h3}
      style={{
        fontSize: '.95rem',
        textDecoration: 'underline'
      }}
    >
      {text}
    </Typography>
  )

  const h2 = (text: string) => (
    <Typography
      variant="h2"
      sx={classes.h2}
      style={{
        fontWeight: 600,
        fontSize:
          screenDimensions.width > breakpoints.mobileSmall ? '1.3rem' : '1.1rem'
      }}
    >
      {text}
    </Typography>
  )

  const link = (text: string, href: string) => (
    <a
      href={href}
      target="_blank"
      style={{
        color: themeColors.info.main
      }}
    >
      {text}
    </a>
  )

  const b2 = (text: string | ReactElement) => (
    <Typography
      variant="body1"
      sx={classes.b2}
      style={{
        fontSize:
          screenDimensions.width > breakpoints.mobileSmall
            ? '0.93rem'
            : '0.85rem'
      }}
    >
      {text}
    </Typography>
  )

  const bullet = (text: string | ReactElement) => (
    <div style={classes.bulletsDiv}>
      <Typography variant="body1" sx={Object.assign(classes.b1, classes.ml)}>
        <b>&#8226;</b>
      </Typography>
      <Typography variant="body1" sx={classes.b1}>
        {text}
      </Typography>
    </div>
  )

  return (
    <Box sx={classes.mainDiv}>
      <Box
        sx={classes.sndDiv}
        style={{
          minHeight: screenDimensions.height - 220
        }}
      >
        <Typography
          variant="h1"
          sx={classes.h1}
          style={{
            fontSize:
              screenDimensions.width > breakpoints.mobileSmall
                ? '2rem'
                : '1.5rem'
          }}
        >
          {t('h1')}
        </Typography>
        <Typography variant="body1" sx={classes.b1}>
          {t('version')}
        </Typography>
        <Typography
          variant="body1"
          sx={classes.b1}
          style={{ marginBottom: '1.5rem' }}
        >
          {t('update')}
        </Typography>

        <section>
          {b2(t('policy.introduction.title'))}
          {b2(t('policy.introduction.body1'))}
          {b2(t('policy.introduction.body2'))}
          {b2(t('policy.introduction.body3'))}
          {b2(t('policy.introduction.body4'))}
          {b2(t('policy.introduction.body5'))}
          {link(t('policy.introduction.link1'), 'http://kroemtech.ch')}
          <br />
          {link(t('policy.introduction.link2'), 'http://dathascy.com')}
          <br />
          {link(t('policy.introduction.link3'), 'https://portal.dathascy.com')}
        </section>

        <section>{h2(t('policy.information.title'))}</section>

        <section>
          {h3(t('policy.information.introduction.title'))}
          {b2(t('policy.information.introduction.body1'))}
          {bullet(t('policy.information.introduction.body2.1'))}
          {bullet(t('policy.information.introduction.body2.2'))}
          {bullet(t('policy.information.introduction.body2.3'))}
          {bullet(t('policy.information.introduction.body2.4'))}
          {b2(t('policy.information.introduction.body3'))}
          {b2(t('policy.information.introduction.body4'))}
        </section>
        <section>
          {h3(t('policy.information.responsible.title'))}
          {b2(t('policy.information.responsible.body'))}
          {b2(t('policy.information.responsible.company'))}
          {b2(t('policy.information.responsible.address1'))}
          {b2(t('policy.information.responsible.address2'))}
          {b2(t('policy.information.responsible.address3'))}
          {b2(t('policy.information.responsible.body2'))}
        </section>
        <section>
          {h3(t('policy.information.collect.title'))}
          {h3Decoration(
            t('policy.information.collect.registrationInformation.subTitle')
          )}
          {b2(t('policy.information.collect.registrationInformation.body'))}
          {h3Decoration(t('policy.information.collect.eCommerce.subTitle'))}
          {b2(t('policy.information.collect.eCommerce.body1'))}
          {b2(t('policy.information.collect.eCommerce.body2.1'))}
          {bullet(t('policy.information.collect.eCommerce.body2.2.1'))}
          {bullet(t('policy.information.collect.eCommerce.body2.2.2'))}
          {b2(t('policy.information.collect.eCommerce.body3'))}
          {b2(t('policy.information.collect.eCommerce.body4'))}
          {bullet(t('policy.information.collect.eCommerce.body5.1'))}
          {bullet(t('policy.information.collect.eCommerce.body5.2'))}
          {bullet(t('policy.information.collect.eCommerce.body5.3'))}
          {bullet(t('policy.information.collect.eCommerce.body5.4'))}
          {b2(t('policy.information.collect.eCommerce.body6'))}
          {b2(t('policy.information.collect.eCommerce.body7'))}
        </section>
        <section>
          {h3Decoration(t('policy.information.collect.logFiles.subTitle'))}
          {b2(t('policy.information.collect.logFiles.body1'))}
          {bullet(t('policy.information.collect.logFiles.body2.1'))}
          {bullet(t('policy.information.collect.logFiles.body2.2'))}
          {bullet(t('policy.information.collect.logFiles.body2.3'))}
          {bullet(t('policy.information.collect.logFiles.body2.4'))}
          {bullet(t('policy.information.collect.logFiles.body2.5'))}
          {bullet(t('policy.information.collect.logFiles.body2.6'))}
          {bullet(t('policy.information.collect.logFiles.body2.7'))}
          {bullet(t('policy.information.collect.logFiles.body2.8'))}
          {b2(t('policy.information.collect.logFiles.body3'))}
        </section>

        <section>
          {h3Decoration(
            t('policy.information.collect.deviceIdentifiers.subTitle')
          )}
          {b2(t('policy.information.collect.deviceIdentifiers.body'))}
        </section>

        <section>
          {h3Decoration(t('policy.information.collect.locationData.subTitle'))}
          {b2(t('policy.information.collect.locationData.body'))}
        </section>

        <section>
          {h3Decoration(t('policy.information.collect.cookies.subTitle'))}
          {b2(t('policy.information.collect.cookies.body'))}
        </section>

        <section>
          {h3(t('policy.information.categories.title'))}
          {bullet(t('policy.information.categories.body.1'))}
          {bullet(t('policy.information.categories.body.2'))}
          {bullet(t('policy.information.categories.body.3'))}
        </section>

        <section>
          {h3(t('policy.information.yourContent.title'))}
          {h3Decoration(t('policy.information.yourContent.uploading.subTitle'))}
          {b2(t('policy.information.yourContent.uploading.body'))}
        </section>

        <section>
          {h3Decoration(t('policy.information.yourContent.useOfContent.title'))}
          {b2(t('policy.information.yourContent.useOfContent.body1'))}
          {b2(t('policy.information.yourContent.useOfContent.body2'))}
          {bullet(t('policy.information.yourContent.useOfContent.body3.1'))}
          {bullet(t('policy.information.yourContent.useOfContent.body3.2'))}
          {bullet(t('policy.information.yourContent.useOfContent.body3.3'))}
          {bullet(t('policy.information.yourContent.useOfContent.body3.4'))}
          {bullet(t('policy.information.yourContent.useOfContent.body3.5'))}
          {b2(t('policy.information.yourContent.useOfContent.body4'))}
        </section>
        <section>
          {h3(t('policy.information.howWeUse.title'))}
          {b2(t('policy.information.howWeUse.body'))}
          {bullet(t('policy.information.howWeUse.body2.1'))}
          {bullet(t('policy.information.howWeUse.body2.2'))}
          {bullet(t('policy.information.howWeUse.body2.3'))}
          {bullet(t('policy.information.howWeUse.body2.4'))}
        </section>

        <section>
          {h3(t('policy.information.legalBasis.title'))}
          {h3Decoration(
            t('policy.information.legalBasis.contractualNecessity.subTitle')
          )}
          {b2(t('policy.information.legalBasis.contractualNecessity.body1'))}
          {b2(t('policy.information.legalBasis.contractualNecessity.body2'))}
          {bullet(
            t('policy.information.legalBasis.contractualNecessity.body3.1')
          )}
          {bullet(
            t('policy.information.legalBasis.contractualNecessity.body3.2')
          )}
          {bullet(
            t('policy.information.legalBasis.contractualNecessity.body3.3')
          )}
        </section>

        <section>
          {h3Decoration(
            t('policy.information.legalBasis.legalObligation.subTitle')
          )}
          {b2(t('policy.information.legalBasis.legalObligation.body'))}
          {bullet(t('policy.information.legalBasis.legalObligation.body2.1'))}
          {bullet(t('policy.information.legalBasis.legalObligation.body2.2'))}
        </section>

        <section>
          {h3Decoration(
            t('policy.information.legalBasis.legitimateInterests.subTitle')
          )}
          {b2(t('policy.information.legalBasis.legitimateInterests.body'))}
          {bullet(
            t('policy.information.legalBasis.legitimateInterests.body2.1')
          )}
          {bullet(
            t('policy.information.legalBasis.legitimateInterests.body2.2')
          )}
        </section>

        <section>
          {h3Decoration(t('policy.information.legalBasis.consent.subTitle'))}
          {b2(t('policy.information.legalBasis.consent.body'))}
        </section>

        <section>
          {h3(t('policy.information.contactMe.title'))}
          {b2(t('policy.information.contactMe.body'))}
          {bullet(t('policy.information.contactMe.body2.1'))}
          {bullet(t('policy.information.contactMe.body2.2'))}
          {bullet(t('policy.information.contactMe.body2.3'))}
          {bullet(t('policy.information.contactMe.body2.4'))}
          {bullet(t('policy.information.contactMe.body2.5'))}
          {bullet(t('policy.information.contactMe.body2.6'))}
          {bullet(t('policy.information.contactMe.body2.7'))}
          {bullet(t('policy.information.contactMe.body2.8'))}
        </section>

        <section>
          {h3(t('policy.information.myPersonalData.title'))}
          {b2(t('policy.information.myPersonalData.body'))}
          {bullet(t('policy.information.myPersonalData.body2.1'))}
          {bullet(t('policy.information.myPersonalData.body2.2'))}
          {bullet(t('policy.information.myPersonalData.body2.3'))}
        </section>

        <section>
          {h3Decoration(
            t('policy.information.myPersonalData.generalUse.subTitle')
          )}
          {b2(t('policy.information.myPersonalData.generalUse.body'))}
          {bullet(t('policy.information.myPersonalData.generalUse.body2.1'))}
          {bullet(t('policy.information.myPersonalData.generalUse.body2.2'))}
          {bullet(t('policy.information.myPersonalData.generalUse.body2.3'))}
        </section>

        <section>
          {h3(t('policy.information.partnerApplications.title'))}
          {b2(t('policy.information.partnerApplications.body'))}
          {b2(t('policy.information.partnerApplications.body2'))}
        </section>

        <section>
          {h3(t('policy.information.trustedService.title'))}
          {b2(t('policy.information.trustedService.body'))}
          {bullet(t('policy.information.trustedService.body2.1'))}
          {bullet(t('policy.information.trustedService.body2.2'))}
          {bullet(t('policy.information.trustedService.body2.3'))}
          {b2(t('policy.information.trustedService.body3'))}
        </section>

        <section>
          {h3(t('policy.information.dataRetention.title'))}
          {b2(t('policy.information.dataRetention.body'))}
          {bullet(t('policy.information.dataRetention.body2.1'))}
          {bullet(t('policy.information.dataRetention.body2.2'))}
          {bullet(t('policy.information.dataRetention.body2.3'))}
        </section>
        <section>{h2(t('policy.dataProcessing.title'))}</section>

        <section>
          {h3(t('policy.dataProcessing.thirdParty.title'))}
          {b2(t('policy.dataProcessing.thirdParty.body'))}
          {b2(t('policy.dataProcessing.thirdParty.body2'))}
        </section>

        <section>
          {h3Decoration(t('policy.dataProcessing.thirdParty.wilio.subTitle'))}
          {b2(t('policy.dataProcessing.thirdParty.wilio.body'))}
          {b2(
            <>
              {t('policy.dataProcessing.thirdParty.wilio.body2')}
              {link(
                'https://www.twilio.com/en-us/legal/privacy',
                'https://www.twilio.com/en-us/legal/privacy '
              )}{' '}
            </>
          )}
        </section>

        <section>
          {h3Decoration(t('policy.dataProcessing.thirdParty.stripe.subTitle'))}
          {b2(t('policy.dataProcessing.thirdParty.stripe.body'))}
          {b2(
            <>
              {t('policy.dataProcessing.thirdParty.stripe.body2')}
              {link(
                'https://stripe.com/en-ch/privacy',
                'https://stripe.com/en-ch/privacy '
              )}{' '}
            </>
          )}
          {b2(t('policy.dataProcessing.thirdParty.stripe.body3'))}
          {b2(t('policy.dataProcessing.thirdParty.stripe.body4'))}
        </section>

        <section>
          {h3(t('policy.dataProcessing.webBrowser.title'))}
          {b2(t('policy.dataProcessing.webBrowser.body'))}
          {b2(t('policy.dataProcessing.webBrowser.body2'))}
        </section>

        <section>
          {h3(t('policy.dataProcessing.HhwWe.title'))}
          {b2(t('policy.dataProcessing.HhwWe.body'))}
          {bullet(t('policy.dataProcessing.HhwWe.body2.1'))}
          {bullet(t('policy.dataProcessing.HhwWe.body2.2'))}
          {bullet(t('policy.dataProcessing.HhwWe.body2.3'))}
          {bullet(t('policy.dataProcessing.HhwWe.body2.4'))}
          {bullet(t('policy.dataProcessing.HhwWe.body2.5'))}
        </section>

        <section>
          {h3(t('policy.dataProcessing.typesOfCookies.title'))}
          {h3Decoration(
            t('policy.dataProcessing.typesOfCookies.sessionCookies.subTitle')
          )}
          {b2(t('policy.dataProcessing.typesOfCookies.sessionCookies.body1'))}
        </section>
        <section>
          {h3Decoration(
            t('policy.dataProcessing.typesOfCookies.persistentCookies.subTitle')
          )}
          {b2(t('policy.dataProcessing.typesOfCookies.persistentCookies.body'))}
        </section>
        <section>
          {h3Decoration(
            t('policy.dataProcessing.typesOfCookies.firstParty.subTitle')
          )}
          {b2(t('policy.dataProcessing.typesOfCookies.firstParty.body'))}
        </section>

        <section>
          {h3Decoration(
            t('policy.dataProcessing.typesOfCookies.thirdPartyCookies.subTitle')
          )}
          {b2(t('policy.dataProcessing.typesOfCookies.thirdPartyCookies.body'))}
        </section>

        <section>
          {h3(t('policy.dataProcessing.thirdPartyTools.title'))}
          {b2(t('policy.dataProcessing.thirdPartyTools.body'))}
          {bullet(t('policy.dataProcessing.thirdPartyTools.body2.1'))}
          {bullet(t('policy.dataProcessing.thirdPartyTools.body2.2'))}
          {bullet(t('policy.dataProcessing.thirdPartyTools.body2.3'))}
          {b2(t('policy.dataProcessing.thirdPartyTools.body3'))}
        </section>

        <section>
          {h3(t('policy.dataProcessing.cookieControl.title'))}
          {b2(t('policy.dataProcessing.cookieControl.body'))}
          {bullet(t('policy.dataProcessing.cookieControl.body2.1'))}
          {bullet(t('policy.dataProcessing.cookieControl.body2.2'))}
          {b2(t('policy.dataProcessing.cookieControl.body3'))}
        </section>

        <section>
          {h3(t('policy.dataProcessing.useOfWeb.title'))}
          {b2(t('policy.dataProcessing.useOfWeb.body1'))}
          {b2(t('policy.dataProcessing.useOfWeb.body2'))}
          {b2(t('policy.dataProcessing.useOfWeb.body3'))}
          {b2(t('policy.dataProcessing.useOfWeb.body4'))}
        </section>
        <section>{h2(t('policy.rightsOfData.title'))}</section>
        <section>
          {b2(t('policy.rightsOfData.body1'))}
          {h3(t('policy.rightsOfData.a.title'))}
          {b2(t('policy.rightsOfData.a.body1'))}
          {h3(t('policy.rightsOfData.b.title'))}
          {b2(t('policy.rightsOfData.b.body1'))}
          {h3(t('policy.rightsOfData.c.title'))}
          {b2(t('policy.rightsOfData.c.body1'))}
          {h3(t('policy.rightsOfData.d.title'))}
          {b2(t('policy.rightsOfData.d.body1'))}
          {bullet(t('policy.rightsOfData.d.bullet1'))}
          {b2(t('policy.rightsOfData.d.body2'))}
          {bullet(t('policy.rightsOfData.d.bullet1'))}
          {b2(t('policy.rightsOfData.d.body3'))}
        </section>
        <section> {h2(t('policy.finalProvisions.title'))}</section>
        <section>
          {h3(t('policy.finalProvisions.security.title'))}
          {b2(t('policy.finalProvisions.security.body1'))}
          {b2(t('policy.finalProvisions.security.body2'))}
        </section>
        <section>
          {h3(t('policy.finalProvisions.updatesToKroemtech.title'))}
          {b2(t('policy.finalProvisions.updatesToKroemtech.body'))}
        </section>
        <section> {h2(t('policy.questionsOrRequests.title'))}</section>
        <section>
          {b2(t('policy.questionsOrRequests.body'))}
          {h3(t('policy.questionsOrRequests.body2'))}
        </section>
      </Box>
    </Box>
  )
}
